import type {
   DocumentExportState,
   ZipExportResponse,
} from '@/types/api-response/zip-export-types.ts';
import { onUnmounted, reactive, ref } from 'vue';
import { useAnalytics } from '@/composables/analytics/analytics-service';
import { attachUiSessionId } from '@/services/ui-session';
import { useAuthStore } from '@/stores/auth-store.ts';

const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;
const POLLING_INTERVAL = 5000; // 5 seconds
const MAX_POLLING_ATTEMPTS = 84; // ~7 minutes timeout (84 * 5s = 420s)
export const EXPORT_LIMIT = 1000;
// Above this count, callers should request cached mode — the live path
// (resolve URLs + download each PDF) doesn't scale past a few dozen docs.
export const CACHED_EXPORT_THRESHOLD = 30;

/**
 * Composable for managing document ZIP export functionality.
 * Handles initiating exports, polling for status, and download URL management.
 */
export function useDocumentExport() {
   const authStore = useAuthStore();
   const { trackEvent } = useAnalytics();

   /**
    * Export state
    */
   const exportState = reactive<DocumentExportState>({
      status: 'idle',
      documentHash: null,
      documentCount: 0,
      downloadUrl: null,
      successfulCount: 0,
      failedDocuments: [],
      errorMessage: null,
      currentStep: null,
   });

   /**
    * Whether the export modal is open
    */
   const isExportModalOpen = ref(false);

   /**
    * Polling timeout ID for cleanup
    */
   let pollingTimeoutId: ReturnType<typeof setTimeout> | null = null;
   let pollingAttempts = 0;

   /**
    * Store document IDs for polling
    */
   let currentDocumentIds: string[] = [];

   /**
    * Cached-mode flag for the current export run; reused on each poll so
    * the server keeps serving the same job.
    */
   let currentPreferCached = false;

   /**
    * Run token to prevent stale async responses from mutating state.
    * Incremented on each new export run; responses check this before updating state.
    */
   let exportRunId = 0;

   function trackExportError(reason: string) {
      trackEvent('zip:export', {
         outcome: 'error',
         document_count: exportState.documentCount,
         error_reason: reason,
      });
   }

   /**
    * Reset export state to initial values
    */
   function resetExportState() {
      exportRunId++;
      exportState.status = 'idle';
      exportState.documentHash = null;
      exportState.documentCount = 0;
      exportState.downloadUrl = null;
      exportState.successfulCount = 0;
      exportState.failedDocuments = [];
      exportState.errorMessage = null;
      exportState.currentStep = null;
      pollingAttempts = 0;
      currentDocumentIds = [];
      currentPreferCached = false;
   }

   /**
    * Stop polling for export status
    */
   function stopPolling() {
      if (pollingTimeoutId) {
         clearTimeout(pollingTimeoutId);
         pollingTimeoutId = null;
      }
   }

   /**
    * Call the export endpoint
    * @param documentIds - Array of document IDs to export
    * @param isInitialRequest - Whether this is the initial request (not a poll)
    * @param preferCachedPdfs - Use cached PDFs (faster, may be stale)
    */
   async function callExportEndpoint(
      documentIds: string[],
      isInitialRequest = false,
      preferCachedPdfs = false,
   ): Promise<ZipExportResponse> {
      const apiKey = authStore.userApiKey?.api_key;

      const headers = new Headers({
         'Content-Type': 'application/json',
      });
      await attachUiSessionId(headers);

      const response = await fetch(`${API_BASE_URL}/nm/nmed/zip_export/export`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            api_key: apiKey,
            document_ids: documentIds,
            force_refresh: isInitialRequest && import.meta.env.DEV, // In development, force refresh on initial request to bypass cache
            prefer_cached_pdfs: preferCachedPdfs,
         }),
      });

      if (!response.ok) {
         const errorData = await response.json().catch(() => ({}));
         if (response.status === 422) {
            throw new Error(errorData.detail || errorData.message || 'Too many documents selected. Please select fewer documents and try again.');
         }
         throw new Error(errorData.message || errorData.detail || `Request failed with status ${response.status}`);
      }

      return response.json();
   }

   /**
    * Update export state from API response
    */
   function updateStateFromResponse(data: ZipExportResponse) {
      exportState.documentHash = data.document_hash;
      exportState.documentCount = data.document_count;
      exportState.currentStep = data.current_step ?? null;

      if (data.status === 'ready') {
         exportState.status = 'ready';
         exportState.downloadUrl = data.download_url || null;
         exportState.successfulCount = data.successful_count ?? data.document_count;
         exportState.failedDocuments = data.failed_documents ?? [];
         trackEvent('zip:export', {
            outcome: 'ok',
            document_count: data.document_count,
            successful_count: exportState.successfulCount,
            failed_count: exportState.failedDocuments.length,
         });
      }
   }

   /**
    * Initiate a document export
    */
   async function initiateExport(
      documentIds: string[],
      options: { preferCachedPdfs?: boolean } = {},
   ): Promise<boolean> {
      const apiKey = authStore.userApiKey?.api_key;
      const preferCachedPdfs = options.preferCachedPdfs ?? false;

      if (!apiKey) {
         exportState.status = 'error';
         exportState.errorMessage = 'API key not found. Please generate an API key in settings.';
         isExportModalOpen.value = true;
         return false;
      }

      if (documentIds.length === 0) {
         exportState.status = 'error';
         exportState.errorMessage = 'No documents selected for export.';
         isExportModalOpen.value = true;
         return false;
      }

      if (documentIds.length > EXPORT_LIMIT) {
         exportState.status = 'error';
         exportState.errorMessage = `Cannot export more than ${EXPORT_LIMIT} documents at once.`;
         isExportModalOpen.value = true;
         return false;
      }

      // Reset state and open modal
      resetExportState();
      exportState.status = 'initiating';
      exportState.documentCount = documentIds.length;
      currentDocumentIds = [...documentIds];
      currentPreferCached = preferCachedPdfs;
      isExportModalOpen.value = true;

      // Capture run ID to detect stale responses
      const runId = exportRunId;

      try {
         const data = await callExportEndpoint(documentIds, true, preferCachedPdfs); // True for initial request avoids caching

         // Bail if this run was cancelled/superseded
         if (runId !== exportRunId) {
            return false;
         }

         updateStateFromResponse(data);

         // Check if ready immediately
         if (data.status === 'ready') {
            return true;
         }

         // Still processing, start polling
         exportState.status = 'processing';
         scheduleNextPoll();
         return true;
      }
      catch (error) {
         // Bail if this run was cancelled/superseded
         if (runId !== exportRunId) {
            return false;
         }

         exportState.status = 'error';
         exportState.errorMessage = error instanceof Error ? error.message : 'Failed to initiate export';
         trackExportError(exportState.errorMessage);
         return false;
      }
   }

   /**
    * Poll for export status by calling the same endpoint again
    */
   async function pollExportStatus(): Promise<void> {
      if (currentDocumentIds.length === 0) {
         return;
      }

      // Capture run ID to detect stale responses
      const runId = exportRunId;

      pollingAttempts++;

      if (pollingAttempts >= MAX_POLLING_ATTEMPTS) {
         // Check run ID before mutating state
         if (runId !== exportRunId) {
            return;
         }
         exportState.status = 'error';
         exportState.errorMessage = 'Export timed out. Please try again.';
         trackExportError(exportState.errorMessage);
         stopPolling();
         return;
      }

      try {
         const data = await callExportEndpoint(currentDocumentIds, false, currentPreferCached);

         // Bail if this run was cancelled/superseded
         if (runId !== exportRunId) {
            return;
         }

         updateStateFromResponse(data);

         if (data.status === 'ready') {
            stopPolling();
         }
         else {
            // Still processing, schedule next poll
            scheduleNextPoll();
         }
      }
      catch (error) {
         // Bail if this run was cancelled/superseded
         if (runId !== exportRunId) {
            return;
         }

         exportState.status = 'error';
         exportState.errorMessage = error instanceof Error ? error.message : 'Failed to check export status';
         trackExportError(exportState.errorMessage);
         stopPolling();
      }
   }

   /**
    * Schedule the next poll after the interval
    */
   function scheduleNextPoll() {
      stopPolling(); // Clear any existing timeout
      pollingTimeoutId = setTimeout(pollExportStatus, POLLING_INTERVAL);
   }

   /**
    * Cancel the export (stops polling and closes modal)
    */
   function cancelExport() {
      stopPolling();
      isExportModalOpen.value = false;
      resetExportState();
   }

   /**
    * Close the modal (after completion or error)
    */
   function closeExportModal() {
      stopPolling();
      isExportModalOpen.value = false;
   }

   /**
    * Download the exported ZIP file
    */
   function downloadExport() {
      if (exportState.downloadUrl) {
         window.open(exportState.downloadUrl, '_blank');
      }
   }

   // Cleanup on unmount
   onUnmounted(() => {
      stopPolling();
   });

   return {
      // State
      exportState,
      isExportModalOpen,

      // Computed helpers
      canExport: () => authStore.isPremiumUser && !!authStore.userApiKey?.api_key,

      // Actions
      initiateExport,
      cancelExport,
      closeExportModal,
      downloadExport,
      resetExportState,
   };
}
